// HalalBadge.tsx

'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'

export interface HalalCertificate {
    number: number
    url: string
}

interface HalalBadgeProps {
    brandName: string
    certificates: HalalCertificate[]
    triggerOnly?: boolean
}

export default function HalalBadge({ brandName, certificates, triggerOnly = false }: HalalBadgeProps) {
    const [isOpen, setIsOpen] = useState(false)
    const hasCertificates = certificates.length > 0

    useEffect(() => {
        if (isOpen) document.body.style.overflow = 'hidden'
        else document.body.style.overflow = ''
        return () => { document.body.style.overflow = '' }
    }, [isOpen])

    // Если triggerOnly и нет сертификатов — ничего не рендерим
    if (triggerOnly && !hasCertificates) return null

    return (
        <>
            {triggerOnly ? (
                <button
                    onClick={() => setIsOpen(true)}
                    className="ml-auto shrink-0 flex items-center justify-center gap-1 px-3 py-[6px] bg-white border border-[#d4ebb0] rounded-lg text-[12px] font-medium text-[#2d5a00] cursor-pointer transition-all duration-150 hover:border-[#64B704] hover:bg-[#e8f5d0] w-[100%]"
                >
                    <span className='md:hidden block'>Сертификаты соответствия продукции бренда</span>
                    <span className='hidden md:block'>Сертификаты</span>
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" className="text-[#64B704]">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                </button>
            ) : (
                // Иконка халяль на карточке — только если есть сертификаты, иначе просто не кликабельная
                hasCertificates ? (
                    <button
                        onClick={() => setIsOpen(true)}
                        title={`${brandName} — Халяль сертифицирован`}
                        className="absolute top-2 right-2 z-10 w-[64px] h-[64px] flex items-center justify-center cursor-pointer border-0 p-0 bg-transparent"
                    >
                        <Image
                            src="/halal-logo.webp"
                            alt="Halal certified"
                            width={46}
                            height={46}
                            className="object-contain drop-shadow- sm !m-0 !bg-transparent p-1"
                        />
                    </button>
                ) : (
                    <div
                        title={`${brandName} — Халяль сертифицирован`}
                        className="absolute top-2 right-2 z-10 w-[64px] h-[64px] flex items-center justify-center border-0 p-0 bg-transparent"
                    >
                        <Image
                            src="/halal-logo.webp"
                            alt="Halal certified"
                            width={46}
                            height={46}
                            className="object-contain drop-shadow- sm !m-0 !bg-transparent p-1"
                        />
                    </div>
                )
            )}

            {isOpen && hasCertificates && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
                    style={{ background: 'rgba(0,0,0,0.35)' }}
                    onClick={() => setIsOpen(false)}
                >
                    <div
                        className="w-full max-w-[320px] bg-white rounded-2xl overflow-hidden"
                        style={{ boxShadow: '0 16px 48px rgba(0,0,0,0.18)' }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Шапка */}
                        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#f0f0f0]">
                            <div className="w-10 h-10 rounded-xl bg-[#f0f7e6] border border-[#d4ebb0] flex items-center justify-center shrink-0">
                                <Image
                                    src="/halal-logo.webp"
                                    alt="Halal"
                                    width={28}
                                    height={28}
                                    className="object-contain !m-0 !bg-transparent p-1"
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[13px] font-semibold text-[#111] m-0 leading-[1.2]">
                                    Халяль сертифицирован
                                </p>
                                <p className="text-[11px] text-[#999] m-0 mt-px">
                                    {brandName}
                                </p>
                            </div>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="w-7 h-7 rounded-lg border border-[#e5e5e5] bg-[#fafafa] cursor-pointer flex items-center justify-center shrink-0 text-[#888] transition-colors duration-150 hover:bg-[#f0f0f0]"
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        {/* Описание */}
                        <div className="px-4 py-3">
                            <p className="text-[12px] text-[#555] m-0 leading-[1.65]">
                                Бренд <span className="font-semibold text-[#111]">{brandName}</span> соответствует
                                стандартам Халяль и имеет действующий сертификат соответствия Halal.
                            </p>
                        </div>

                        {/* Сертификаты */}
                        <div className="px-4 pb-4 flex flex-col gap-[6px]">
                            <p className="text-[10px] text-[#bbb] m-0 mb-1 font-medium tracking-[0.04em] uppercase">
                                Сертификаты
                            </p>
                            {certificates.map((cert) => (
                                <a key={cert.number}
                                    href={cert.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-[10px] px-3 py-[9px] bg-[#fafafa] border border-[#e8e8e8] rounded-xl no-underline transition-all duration-150 hover:border-[#64B704] hover:bg-[#f0f7e6]"
                                >
                                    <div className="w-7 h-7 rounded-lg bg-[#f0f7e6] border border-[#d4ebb0] flex items-center justify-center shrink-0">
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64B704" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                        </svg>
                                    </div>
                                    <span className="text-[12px] font-medium text-[#333]">
                                        Сертификат #{cert.number}
                                    </span>
                                    <svg className="w-3 h-3 text-[#ccc] ml-auto shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                    </svg>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}